<script lang="ts">
  /**
   * The shell: backdrop, screen switching, toast.
   *
   * Screens are mounted one at a time rather than all rendered and hidden. The
   * prototype could afford `display:none` because its screens were static
   * markup; here the draft holds a live feasibility search and the match holds
   * a running timer, and keeping either mounted behind the other would have it
   * doing work nobody is looking at.
   */
  import Icons from './components/Icons.svelte';
  import TimeoutSheet from './components/TimeoutSheet.svelte';
  import SeasonSheet from './components/SeasonSheet.svelte';
  import LegalSheet from './components/LegalSheet.svelte';
  import Home from './screens/Home.svelte';
  import DraftScreen from './screens/Draft.svelte';
  import Squad from './screens/Squad.svelte';
  import Toss from './screens/Toss.svelte';
  import BowlerSelect from './screens/BowlerSelect.svelte';
  import MatchScreen from './screens/MatchScreen.svelte';
  import InningsBreak from './screens/InningsBreak.svelte';
  import Result from './screens/Result.svelte';
  import Scorecard from './screens/Scorecard.svelte';
  import RoomLobby from './screens/RoomLobby.svelte';
  import RoomInningsDone from './screens/RoomInningsDone.svelte';
  import RoomResult from './screens/RoomResult.svelte';
  import { Draft, FREE_RESPINS } from './lib/draft.svelte';
  import { Match } from './lib/match.svelte';
  import { draftOpponent } from './lib/cpu';
  import { getCoefficients } from './lib/coefficients';
  import { replayRoom } from './lib/replay';
  import {
    createRoom as apiCreateRoom,
    joinRoom as apiJoinRoom,
    fetchRoom,
    extendRoom as apiExtendRoom,
    submitSquad as apiSubmitSquad,
    submitInnings as apiSubmitInnings,
    RoomPoller,
    RoomError,
    rememberRoom,
    forgetRoom,
    rememberedRoom,
    type RoomView,
  } from './lib/room.svelte';
  import type { Roster } from './lib/roster';
  import type { InningsResult } from '@ipl-sim/engine-cricket';
  import { track, setCoefficientVersion } from './lib/analytics';

  interface Props {
    roster: Roster;
  }
  let { roster }: Props = $props();

  type Screen = 'home' | 'draft' | 'squad' | 'match' | 'scorecard' | 'lobby' | 'roomresult';

  let screen = $state<Screen>('home');

  /**
   * Created on demand, not at mount. Constructing a Draft decodes and indexes
   * every squad in the year range, which is wasted work for anyone who opens
   * the app and reads the home screen.
   */
  let draft = $state<Draft | null>(null);
  let match = $state<Match | null>(null);
  /**
   * The season picker is shown once, before the first pick. The copy promises
   * the range is "locked once you start", and it is: nothing reopens this, and
   * `Draft.setRange` is not reachable from anywhere else.
   */
  let choosingSeasons = $state(false);
  /**
   * Deep-linkable at #legal so the policy can be cited from outside the app —
   * an app store listing or an AdSense review both want a URL, not "tap the
   * footer".
   */
  let showingLegal = $state(location.hash === '#legal');

  // ── 1v1 ──────────────────────────────────────────────────────────────────
  //
  // A room is the source of truth for whose turn it is; nothing here decides
  // it. The local Draft and Match are just the way this device takes its turn.
  let roomView = $state<RoomView | null>(null);
  let roomBusy = $state(false);
  let roomError = $state('');
  let poller: RoomPoller | null = null;
  /**
   * The opponent's innings, rebuilt locally from their decision log.
   *
   * The room never stored a ball of it — the engine is deterministic, so ~1 KB
   * of decisions reproduces the whole scorecard. Held here because replaying is
   * cheap but not free, and the scorecard is opened deliberately.
   */
  let roomScorecard = $state<{ name: string; result: InningsResult }[]>([]);

  /** Keeps the poller running only while the room is genuinely waiting. */
  function watchRoom(view: RoomView): void {
    roomView = view;
    rememberRoom(view.code);
    poller?.stop();
    poller = new RoomPoller(
      view.code,
      (next) => (roomView = next),
      () => {
        /* a failed poll is not worth interrupting the screen for */
      },
    );
    poller.start();
  }

  function leaveRoom(): void {
    poller?.stop();
    poller = null;
    roomView = null;
    roomError = '';
    forgetRoom();
  }

  async function roomAction<T>(fn: () => Promise<RoomView>): Promise<boolean> {
    roomBusy = true;
    roomError = '';
    try {
      watchRoom(await fn());
      return true;
    } catch (error) {
      roomError =
        error instanceof RoomError ? error.message : 'Something went wrong. Try again.';
      return false;
    } finally {
      roomBusy = false;
    }
  }

  /**
   * Drafting for a room, seed-locked.
   *
   * The room's seed drives the squad sequence and the room's allowance sets the
   * re-spins, so both players are offered identical squads in identical order
   * with identical tokens. That is the whole fairness argument for 1v1.
   */
  function draftForRoom(): void {
    const view = roomView;
    if (!view) return;
    draft = new Draft(roster, view.seed);
    draft.respinsLeft = view.respinAllowance;
    draftStartedAt = Date.now();
    // No season picker: the range is part of the seeded sequence, and letting
    // one side narrow it would break the lock.
    choosingSeasons = false;
    screen = 'draft';
  }

  async function submitRoomSquad(): Promise<void> {
    const view = roomView;
    const d = draft;
    if (!view || !d) return;
    const xi = d.xi.map((row) => row.player!).filter(Boolean);
    const ok = await roomAction(() => apiSubmitSquad(view.code, xi, d.subs.filter(Boolean) as never));
    if (ok) {
      draft = null;
      screen = 'lobby';
    }
  }

  /** Plays this device's innings. The opponent's happens on their phone. */
  async function playRoomInnings(): Promise<void> {
    const view = roomView;
    if (!view || !view.yourXI || !view.opponentXI) return;

    let coefficients;
    try {
      coefficients = await getCoefficients();
      setCoefficientVersion(coefficients.version);
    } catch {
      toast('Could not start the match — check your connection');
      return;
    }

    const superOver = view.status === 'SUPER_OVER';
    // In a super over the position within the round is what decides whether
    // this side is setting or chasing.
    const chasing = superOver ? view.superOvers.length % 2 === 1 : view.status === 'INNINGS_2';
    const them = view.you === 'host' ? view.guest : view.host;

    match?.dispose();
    match = new Match(
      (view.you === 'host' ? view.host : view.guest)?.teamName ?? 'You',
      view.yourXI as never,
      { name: them?.teamName ?? 'Opponent', xi: view.opponentXI as never, subs: [] },
      view.seed,
      coefficients,
      {
        inningsIndex: chasing ? 1 : 0,
        target: chasing ? (view.target ?? undefined) : undefined,
        superOver,
        seed: superOver ? (view.superOverSeed ?? undefined) : undefined,
      },
    );
    screen = 'match';
  }

  /** Sends the finished innings to the room, then returns to the lobby. */
  async function submitRoomInnings(): Promise<void> {
    const view = roomView;
    const submission = match?.submission();
    if (!view || !submission) return;

    const ok = await roomAction(() => apiSubmitInnings(view.code, submission));
    if (ok) {
      match?.dispose();
      match = null;
      screen = 'lobby';
    } else {
      // A rejected innings is the cheat path, and it should never happen to an
      // honest client. Saying so plainly beats a silent return to the lobby.
      toast(roomError || 'Your innings could not be verified');
    }
  }

  /**
   * `#join=CODE` from a shared invite. Read once, then cleared so a reload does
   * not try to re-join a room the user has since left.
   */
  $effect(() => {
    const invite = /^#join=([A-Z2-9]{6})$/i.exec(location.hash);
    if (invite) {
      history.replaceState(null, '', location.pathname);
      screen = 'lobby';
      void roomAction(() => fetchRoom(invite[1].toUpperCase()));
      return;
    }

    // No invite: pick up a room this device was already in. Silent on failure --
    // an expired or deleted room should leave the player on Home, not on an
    // error they did not ask for.
    const remembered = rememberedRoom();
    if (!remembered) return;
    void fetchRoom(remembered)
      .then((view) => {
        watchRoom(view);
        // A finished room resumes straight to its result -- the match you just
        // played is the thing you came back for. An expired one resumes to the
        // lobby, where the host is offered an extension. Neither is forgotten
        // here; leaving the room is what clears it, so nothing is stranded.
        screen = view.status === 'DONE' ? 'roomresult' : 'lobby';
      })
      .catch(() => forgetRoom());
  });
  /** Where "Back" returns to from the scorecard — it is reachable from two places. */
  let scorecardReturn = $state<Screen>('match');

  let toastMessage = $state('');
  let toastTimer: ReturnType<typeof setTimeout> | undefined;

  function toast(message: string): void {
    toastMessage = message;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (toastMessage = ''), 2600);
  }

  /** When the current draft began, for the funnel's elapsed times. */
  let draftStartedAt = 0;

  /**
   * A draft the user walked away from. Called before anything replaces it, so
   * both "went home" and "started over" are counted -- abandonment by pick
   * number is the chart that says where the draft loses people.
   */
  function abandonDraft(): void {
    if (!draft || draft.isComplete) return;
    track('draft_abandoned', {
      pickNumber: draft.count.have,
      elapsedMs: Date.now() - draftStartedAt,
    });
  }

  function startDraft(): void {
    // A fresh draft each time, or backing out to Home and starting again would
    // silently resume a half-finished squad.
    abandonDraft();
    match?.dispose();
    match = null;
    draft = new Draft(roster);
    draftStartedAt = Date.now();
    choosingSeasons = true;
    screen = 'draft';
  }

  /**
   * The coefficients are fetched here rather than at boot: this is the first
   * moment they are actually needed, and it keeps them off the critical path
   * for anyone who opens the app and reads the home screen.
   */
  async function startMatch(): Promise<void> {
    const d = draft;
    if (!d) return;

    let coefficients;
    try {
      coefficients = await getCoefficients();
      setCoefficientVersion(coefficients.version);
    } catch {
      // Refusing to play beats simulating with invented numbers: that would
      // produce a match the server cannot replay, which is indistinguishable
      // from a tampered result.
      toast('Could not start the match — check your connection');
      return;
    }

    const seed = crypto.randomUUID();
    // The opponent is drafted through the same guard the user just used, from
    // the same year range, so it is always a legal side and always drawn from
    // the same era the user chose.
    const opponent = draftOpponent(roster, d.fromYear, d.toYear, `${seed}:cpu`);
    const xi = d.xi.map((row) => row.player!).filter(Boolean);

    track('team_named');

    match?.dispose();
    match = new Match(d.teamName.trim(), xi, opponent, seed, coefficients);
    screen = 'match';
  }

  function goHome(): void {
    abandonDraft();
    match?.dispose();
    match = null;
    draft = null;
    leaveRoom();
    screen = 'home';
  }

  /** Both innings, in batting order, for the full scorecard. */
  const scorecardInnings = $derived.by<{ name: string; result: InningsResult }[]>(() => {
    const m = match;
    if (!m) return [];
    const out: { name: string; result: InningsResult }[] = [];
    const firstName = m.youBatFirst ? m.youName : m.opponent.name;
    const secondName = m.youBatFirst ? m.opponent.name : m.youName;
    if (m.firstInnings) out.push({ name: firstName, result: m.firstInnings });
    if (m.secondInnings) out.push({ name: secondName, result: m.secondInnings });
    return out;
  });
</script>

<Icons />

<div class="app" id="app">
  <!--
    One backdrop, reused on every screen. Portrait crop below 1024px, landscape
    original above — see design/README.md. The breakpoint is not arbitrary:
    <source media> keys to the viewport, and .app is a fixed 390px frame until
    exactly 1024px.
  -->
  <div class="stadium" aria-hidden="true">
    <picture>
      <source
        media="(min-width: 1024px)"
        type="image/avif"
        srcset="/img/stadium-688.avif 688w, /img/stadium-1376.avif 1376w"
        sizes="100vw"
      />
      <source
        media="(min-width: 1024px)"
        type="image/webp"
        srcset="/img/stadium-688.webp 688w, /img/stadium-1376.webp 1376w"
        sizes="100vw"
      />
      <source
        type="image/avif"
        srcset="/img/stadium-p384.avif 384w, /img/stadium-p768.avif 768w"
        sizes="100vw"
      />
      <source
        type="image/webp"
        srcset="/img/stadium-p384.webp 384w, /img/stadium-p768.webp 768w"
        sizes="100vw"
      />
      <img
        src="/img/stadium-p768.webp"
        alt=""
        width="768"
        height="1376"
        fetchpriority="high"
        decoding="async"
      />
    </picture>
  </div>

  {#if screen === 'roomresult' && roomView}
    <RoomResult
      view={roomView}
      onScorecard={async () => {
        try {
          const coefficients = await getCoefficients();
          roomScorecard = replayRoom(roomView!, coefficients);
          if (roomScorecard.length === 0) {
            toast('That scorecard could not be rebuilt');
            return;
          }
          scorecardReturn = 'roomresult';
          screen = 'scorecard';
        } catch {
          toast('Could not load the scorecard');
        }
      }}
      onRematch={() => {
        // A fresh room, not a rematch in the old one: the seed is what makes a
        // room fair, and reusing it would offer both players the squads they
        // already know.
        leaveRoom();
        screen = 'lobby';
      }}
      onHome={goHome}
    />
  {:else if screen === 'lobby'}
    <RoomLobby
      view={roomView}
      busy={roomBusy}
      error={roomError}
      onCreate={(name) => roomAction(() => apiCreateRoom(name))}
      onJoin={(code, name) => roomAction(() => apiJoinRoom(code, name))}
      onDraft={draftForRoom}
      onPlay={playRoomInnings}
      onResult={() => (screen = 'roomresult')}
      onExtend={() => roomAction(() => apiExtendRoom(roomView!.code))}
      onHome={goHome}
      ontoast={toast}
    />
  {:else if screen === 'scorecard'}
    <Scorecard
      innings={roomScorecard.length > 0 ? roomScorecard : scorecardInnings}
      onback={() => {
        roomScorecard = [];
        screen = scorecardReturn;
      }}
    />
  {:else if screen === 'match' && match}
    <!--
      One object, five screens. Which one shows is a function of the match's own
      stage and whether it is waiting on the user, so the UI can never disagree
      with the engine about whose turn it is.
    -->
    {#if match.stage === 'TOSS'}
      <Toss {match} />
    {:else if match.stage === 'INNINGS_BREAK'}
      <InningsBreak {match} />
    {:else if match.stage === 'DONE' && match.solo}
      <RoomInningsDone {match} busy={roomBusy} onsubmit={submitRoomInnings} />
    {:else if match.stage === 'DONE'}
      <Result
        {match}
        onscorecard={() => {
          scorecardReturn = 'match';
          screen = 'scorecard';
        }}
        onagain={startDraft}
        onhome={goHome}
      />
    {:else if match.prompt?.kind === 'SELECT_BOWLER'}
      <BowlerSelect {match} />
    {:else}
      <MatchScreen
        {match}
        onscorecard={() => {
          scorecardReturn = 'match';
          screen = 'scorecard';
        }}
      />
    {/if}
  {:else if screen === 'draft' && draft}
    <DraftScreen
      {draft}
      onnext={() => {
        track('draft_completed', {
          elapsedMs: Date.now() - draftStartedAt,
          overseas: draft!.picked.filter((p) => !p.isIndian).length,
          bowlers: draft!.picked.filter((p) => p.bowlOvers >= 1).length,
          capWinners: draft!.picked.filter((p) => p.isOrangeCap || p.isPurpleCap).length,
          respinsUsed: FREE_RESPINS - draft!.respinsLeft,
        });
        screen = 'squad';
      }}
      ontoast={toast}
    />
  {:else if screen === 'squad' && draft}
    <Squad
      {draft}
      onback={() => (screen = 'draft')}
      onnext={roomView ? submitRoomSquad : startMatch}
      nextLabel={roomView ? 'Lock in your side' : undefined}
    />
  {:else}
    <Home
      onstart={startDraft}
      onlegal={() => (showingLegal = true)}
      onversus={() => {
        leaveRoom();
        screen = 'lobby';
      }}
      ontoast={toast}
    />
  {/if}

  {#if showingLegal}
    <LegalSheet
      onclose={() => {
        showingLegal = false;
        if (location.hash === '#legal') history.replaceState(null, '', location.pathname);
      }}
    />
  {/if}

  {#if choosingSeasons && draft}
    <SeasonSheet
      {roster}
      from={draft.fromYear}
      to={draft.toYear}
      onconfirm={(from, to) => {
        draft!.setRange(from, to);
        choosingSeasons = false;
        track('year_range_confirmed', { fromYear: from, toYear: to });
      }}
    />
  {/if}

  {#if match}
    <TimeoutSheet {match} />
  {/if}

  {#if toastMessage}
    <div class="toast" role="status">{toastMessage}</div>
  {/if}
</div>
